import styled from 'styled-components';
import axios, { AxiosResponse } from 'axios';
import { TextField, useMediaQuery } from '@mui/material';
import { TFormValues } from '../../App';
import { StepContainer } from '../StepContainer';
import { Footnote } from '../Footnote';
import { SubTitle } from '../SubTitle';
import { Button } from '../Button';
import { Row } from '../Row';
import { breakpoints } from '../../constants';
import { IOption } from '../../types';

interface IProps {
    className?: string;
    formValues: TFormValues;
    options: IOption[];
    showDeleteDisclaimer: boolean;
    formFields?: string[];
    getFieldError: (fieldName: keyof TFormValues) => string | undefined;
    onStepButtonClick(option: IOption): void;
    setFieldValue(field: keyof TFormValues, value: string): void;
    setCurrentFieldsTouched(formFields: string[]): void;
    validateForm(): void;
}

interface IPostcodeRequest {
    zipCode: string;
    houseNumber: string;
}

interface IPostcodeResponse {
    city: string;
    street: string;
}

const BaseAddressForm = ({
    className,
    formValues,
    formFields,
    options,
    showDeleteDisclaimer,
    getFieldError,
    onStepButtonClick,
    setFieldValue,
    setCurrentFieldsTouched,
    validateForm,
}: IProps) => {
    const mediumBreakpoint = useMediaQuery(`(${breakpoints.mediumMin})`);

    const { street, houseNumber, houseNumberAddition, city, zipcode } =
        formValues;

    const handleHouseNumberBlur = () => {
        if (
            !process.env.REACT_APP_POSTCODE_API_URL ||
            !formValues.zipcode ||
            !formValues.houseNumber ||
            !formFields
        )
            return;

        axios
            .post<IPostcodeRequest, AxiosResponse<IPostcodeResponse>>(
                process.env.REACT_APP_POSTCODE_API_URL,
                {
                    zipCode: formValues.zipcode,
                    houseNumber: formValues.houseNumber,
                }
            )
            .then((res) => {
                const { street, city } = res.data;

                setFieldValue('street', street);
                setFieldValue('city', city);

                // Setting touched fields and validating manually
                // preventing form being initially invalid after auto-filling
                setCurrentFieldsTouched(formFields);
                validateForm();
            });
    };

    const subTitleText = showDeleteDisclaimer
        ? 'We hebben onderstaande adresgegevens nodig om je te identificeren zodat wij de juiste persoon uit onze systemen kunnen verwijderen.'
        : 'We zullen je gegevens enkel gebruiken voor het afhandelen van je vraag.';

    return (
        <StepContainer className={className}>
            <h3>
                Wat zijn je <strong>adresgegevens</strong>?
            </h3>
            <SubTitle>{subTitleText}</SubTitle>
            <Row>
                <TextField
                    error={!!getFieldError('zipcode')}
                    helperText={getFieldError('zipcode')}
                    id="zipcode"
                    label="Postcode *"
                    value={zipcode}
                    onChange={(e) => setFieldValue('zipcode', e.target.value)}
                />
                <TextField
                    error={!!getFieldError('houseNumber')}
                    helperText={getFieldError('houseNumber')}
                    id="houseNumber"
                    label="Huisnummer *"
                    sx={{ margin: `${mediumBreakpoint && '0 10px'}` }}
                    value={houseNumber}
                    onBlur={handleHouseNumberBlur}
                    onChange={(e) =>
                        setFieldValue('houseNumber', e.target.value)
                    }
                />
                <TextField
                    error={!!getFieldError('houseNumberAddition')}
                    helperText={getFieldError('houseNumberAddition')}
                    id="houseNumberAddition"
                    label="Toevoeging"
                    value={houseNumberAddition}
                    onChange={(e) =>
                        setFieldValue('houseNumberAddition', e.target.value)
                    }
                />
            </Row>
            <TextField
                error={!!getFieldError('street')}
                helperText={getFieldError('street')}
                id="street"
                label="Straat *"
                value={street}
                onChange={(e) => setFieldValue('street', e.target.value)}
            />
            <TextField
                error={!!getFieldError('city')}
                helperText={getFieldError('city')}
                id="city"
                label="Plaats *"
                value={city}
                onChange={(e) => setFieldValue('city', e.target.value)}
            />
            <Footnote>* Verplicht in te vullen</Footnote>
            {options.map((option) => (
                <Button
                    key={option.title}
                    onClick={() => onStepButtonClick(option)}
                >
                    {option.title}
                </Button>
            ))}
        </StepContainer>
    );
};

export const AddressForm = styled(BaseAddressForm)`
    ${Button} {
        width: 50%;
        margin-top: 24px;

        @media (${breakpoints.mediumMin}) {
            width: fit-content;
        }
    }

    ${Footnote} {
        margin-top: 24px;

        @media (${breakpoints.mediumMin}) {
            margin-left: auto;
            margin-top: 0;
        }
    }
`;
